import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaSave, FaTimes, FaChartBar, FaDrawPolygon, FaHeading, FaBold, FaItalic, FaListUl, FaListOl, FaLink, FaImage, FaCode, FaQuoteRight } from 'react-icons/fa';
import { useNotes } from '../context/NoteContext';
import ObsidianEditor from '../components/ObsidianEditor';
import { useToast } from '../hooks/use-toast';
import excalidrawService from '../services/excalidrawService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 0;
  margin: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: ${props => props.primary ? '#007bff' : props.danger ? '#dc3545' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.primary ? '#0069d9' : props.danger ? '#c82333' : '#5a6268'};
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const TopBar = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: ${props => props.flex || 1};
`;

const TitleInput = styled.input`
  font-size: 1.25rem;
  padding: 0.4rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  
  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const CategorySelect = styled.select`
  padding: 0.4rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  
  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const TagsInput = styled.input`
  padding: 0.4rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  
  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: #495057;
`;

const EditorContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border-top: 1px solid #e0e0e0;
  gap: 0.25rem;
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background-color: transparent;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(0, 123, 255, 0.1);
    color: #007bff;
  }
  
  svg {
    font-size: 1rem;
  }
`;

const ToolbarDivider = styled.div`
  height: 1.5rem;
  width: 1px;
  background-color: #dee2e6;
  margin: 0 0.5rem;
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const NoteEditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateNote, fetchNoteById, loading, categories } = useNotes();
  const { toast } = useToast();
  
  const [note, setNote] = useState({
    title: '',
    content: '',
    categoryId: null,
    tags: [],
  });
  
  const [tagsInput, setTagsInput] = useState('');
  const editorRef = useRef(null);
  
  useEffect(() => {
    const loadNote = async () => {
      if (id !== 'new') {
        try {
          const fetchedNote = await fetchNoteById(id);
          setNote(fetchedNote);
          setTagsInput(fetchedNote.tags ? fetchedNote.tags.join(', ') : '');
        } catch (error) {
          console.error('Error loading note:', error);
          toast({
            title: 'Failed to load note',
            variant: 'destructive',
          });
          navigate('/notes');
        }
      }
    };
    
    loadNote();
  }, [id, fetchNoteById, navigate, toast]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNote(prev => ({ ...prev, [name]: value }));
  };
  
  const handleTagsChange = (e) => {
    setTagsInput(e.target.value);
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    setNote(prev => ({ ...prev, tags }));
  };
  
  const handleContentChange = (content) => {
    setNote(prev => ({ ...prev, content }));
  };
  
  const handleSave = async () => {
    if (!note.title.trim()) {
      toast({
        title: 'Please provide a title',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      await updateNote({
        ...note,
        id: id !== 'new' ? id : undefined,
      });
      
      toast({
        title: 'Note saved successfully',
        variant: 'default',
      });
      
      navigate('/notes');
    } catch (error) {
      console.error('Error saving note:', error);
      toast({
        title: 'Failed to save note',
        variant: 'destructive',
      });
    }
  };
  
  const handleCancel = () => {
    navigate('/notes');
  };
  
  const insertMarkdown = (markdown) => {
    if (editorRef.current) {
      // Focus the editor first, which will set activeLineIndex
      if (document.activeElement !== editorRef.current) {
        editorRef.current.focus();
        // Use setTimeout to ensure focus is set before insertion
        setTimeout(() => {
          editorRef.current.insertMarkdown(markdown);
        }, 0);
      } else {
        // Editor already has focus, insert directly
        editorRef.current.insertMarkdown(markdown);
      }
    }
  };
  
  const handleInsertMermaid = () => {
    const mermaidTemplate = `\`\`\`mermaid
graph TD
  A[Start] --> B[Process]
  B --> C[End]
\`\`\``;
    insertMarkdown(mermaidTemplate);
  };
  
  const handleInsertExcalidraw = async () => {
    if (editorRef.current) {
      try {
        // Get the active line index at the start of the process
        const targetLineIndex = editorRef.current.getLastClickedLineIndex();
        //console.log("Capturing line index before creating sketch:", targetLineIndex);
        
        // Show loading state
        toast({
          title: "Creating sketch...",
          variant: "default",
        });
        
        // Create an empty excalidraw sketch on the server
        const initialData = {
          type: "excalidraw",
          version: 2,
          source: "https://excalidraw.com",
          elements: [],
          appState: {
            viewBackgroundColor: "#ffffff",
            gridSize: null
          }
        };
        
        // Save to server - pass the current note ID if we're editing an existing note
        const noteId = id !== 'new' ? id : null;
        const response = await excalidrawService.saveSketch(initialData, noteId);
        
        // Extract the unique ID from the response
        let sketchId;
        if (response && response.unique_id) {
          sketchId = response.unique_id;
        } else {
          console.error('Unexpected response format:', response);
          throw new Error('Failed to create sketch: No ID returned from server');
        }
        
        //console.log('Created sketch with ID:', sketchId);
        
        // First, for safety, click on the line we want to insert at to ensure it's selected
        if (typeof targetLineIndex === 'number') {
          // Simulate a click on the target line
          const targetLine = document.querySelector(`[lineindex="${targetLineIndex}"]`);
          if (targetLine) {
            //console.log('Found target line element, ensuring it is clicked:', targetLine);
            targetLine.click();
            
            // Wait a tiny bit to ensure the click is registered
            setTimeout(() => {
              //console.log('Now inserting excalidraw block at index:', targetLineIndex);
              editorRef.current.insertExcalidrawBlock(sketchId, targetLineIndex);
              toast({
                title: "Excalidraw block added",
                variant: "default",
              });
            }, 0);
          } else {
            // No target line found, just insert directly
            //console.log('Target line element not found, inserting directly with index:', targetLineIndex);
            editorRef.current.insertExcalidrawBlock(sketchId, targetLineIndex);
            toast({
              title: "Excalidraw block added",
              variant: "default",
            });
          }
        } else {
          // No valid targetLineIndex, just insert
          //console.log('No valid target line index, inserting without specific target');
          editorRef.current.insertExcalidrawBlock(sketchId);
          toast({
            title: "Excalidraw block added",
            variant: "default",
          });
        }
      } catch (error) {
        console.error('Error creating excalidraw sketch:', error);
        toast({
          title: "Failed to create sketch",
          description: error.message || "Please try again",
          variant: "destructive",
        });
      }
    }
  };
  
  const handleToolbarClick = (type) => {
    switch (type) {
      case 'heading':
        insertMarkdown('# ');
        break;
      case 'bold':
        insertMarkdown('**bold text**');
        break;
      case 'italic':
        insertMarkdown('*italic text*');
        break;
      case 'unordered-list':
        insertMarkdown('- ');
        break;
      case 'ordered-list':
        insertMarkdown('1. ');
        break;
      case 'link':
        insertMarkdown('[link text](url)');
        break;
      case 'image':
        insertMarkdown('![alt text](image-url)');
        break;
      case 'code':
        insertMarkdown('`code`');
        break;
      case 'blockquote':
        insertMarkdown('> ');
        break;
      default:
        break;
    }
  };
  
  if (loading && id !== 'new') {
    return <LoadingState>Loading note...</LoadingState>;
  }
  
  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>{id === 'new' ? 'Create New Note' : 'Edit Note'}</Title>
        </HeaderLeft>
        <HeaderRight>
          <ButtonGroup>
            <Button onClick={handleCancel}>
              <FaTimes />
              Cancel
            </Button>
            <Button primary onClick={handleSave}>
              <FaSave />
              Save
            </Button>
          </ButtonGroup>
        </HeaderRight>
      </Header>
      
      <TopBar>
        <InputGroup flex={2}>
          <Label>Title</Label>
          <TitleInput
            name="title"
            value={note.title}
            onChange={handleInputChange}
            placeholder="Note title"
          />
        </InputGroup>
        
        <InputGroup>
          <Label>Category</Label>
          <CategorySelect
            name="categoryId"
            value={note.categoryId || ''}
            onChange={handleInputChange}
          >
            <option value="">No Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </CategorySelect>
        </InputGroup>
        
        <InputGroup flex={2}>
          <Label>Tags (comma separated)</Label>
          <TagsInput
            value={tagsInput}
            onChange={handleTagsChange}
            placeholder="Enter tags separated by commas"
          />
        </InputGroup>
      </TopBar>
      
      <EditorContainer>
        <ObsidianEditor
          value={note.content}
          onChange={handleContentChange}
          onSave={handleSave}
          ref={editorRef}
        />
      </EditorContainer>
      
      <Toolbar>
        <ToolbarSection>
          <ToolbarButton title="Heading" onClick={() => handleToolbarClick('heading')}>
            <FaHeading />
          </ToolbarButton>
          <ToolbarButton title="Bold" onClick={() => handleToolbarClick('bold')}>
            <FaBold />
          </ToolbarButton>
          <ToolbarButton title="Italic" onClick={() => handleToolbarClick('italic')}>
            <FaItalic />
          </ToolbarButton>
        </ToolbarSection>
        
        <ToolbarDivider />
        
        <ToolbarSection>
          <ToolbarButton title="Unordered List" onClick={() => handleToolbarClick('unordered-list')}>
            <FaListUl />
          </ToolbarButton>
          <ToolbarButton title="Ordered List" onClick={() => handleToolbarClick('ordered-list')}>
            <FaListOl />
          </ToolbarButton>
        </ToolbarSection>
        
        <ToolbarDivider />
        
        <ToolbarSection>
          <ToolbarButton title="Link" onClick={() => handleToolbarClick('link')}>
            <FaLink />
          </ToolbarButton>
          <ToolbarButton title="Image" onClick={() => handleToolbarClick('image')}>
            <FaImage />
          </ToolbarButton>
          <ToolbarButton title="Code" onClick={() => handleToolbarClick('code')}>
            <FaCode />
          </ToolbarButton>
          <ToolbarButton title="Blockquote" onClick={() => handleToolbarClick('blockquote')}>
            <FaQuoteRight />
          </ToolbarButton>
        </ToolbarSection>
        
        <ToolbarDivider />
        
        <ToolbarSection>
          <ToolbarButton title="Insert Mermaid Diagram" onClick={handleInsertMermaid}>
            <FaChartBar />
          </ToolbarButton>
          <ToolbarButton title="Insert Excalidraw Sketch" onClick={handleInsertExcalidraw}>
            <FaDrawPolygon />
          </ToolbarButton>
        </ToolbarSection>
      </Toolbar>
    </Container>
  );
};

export default NoteEditScreen;